import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ColorSchemeToggle from './ColorSchemeToggle';
import Button from '@mui/joy/Button';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useAuth } from '../auth/AuthContext';
import { getInitials } from '../utils/getInitials';

type NavItem = {
  readonly path: string;
  readonly label: string;
  readonly icon: React.ReactNode;
};

const navItems: NavItem[] = [
  { path: '/', label: 'Inicio', icon: <HomeOutlinedIcon /> },
  { path: '/chat', label: 'Chat', icon: <ChatBubbleOutlineIcon /> },
  { path: '/clients', label: 'Contactos', icon: <PeopleOutlineIcon /> },
  { path: '/products', label: 'Productos', icon: <Inventory2OutlinedIcon /> },
  { path: '/sales', label: 'Ventas', icon: <SellOutlinedIcon /> },
  { path: '/settings', label: 'Configuración', icon: <SettingsOutlinedIcon /> },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, companyName, logout } = useAuth();
  const [menuQuery, setMenuQuery] = useState('');
  const filteredItems = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [menuQuery]);

  return (
    <Sheet
      variant="outlined"
      sx={{
        width: { xs: 220, sm: 280 },
        flexShrink: 0,
        borderRadius: 0,
        borderTop: 'none',
        borderBottom: 'none',
        borderLeft: 'none',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.surface',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography level="title-lg" sx={{ color: 'primary.500', fontWeight: 'xl' }}>
          WhapBuss
        </Typography>
        <ColorSchemeToggle />
      </Box>
      <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
        <Input
          size="sm"
          placeholder="Buscar en el menú…"
          value={menuQuery}
          onChange={(e) => setMenuQuery(e.target.value)}
          startDecorator={<SearchRoundedIcon />}
          sx={{ borderRadius: 'md' }}
        />
      </Box>
      <List size="sm" sx={{ '--ListItem-radius': '8px', px: 1.5, py: 1, flex: 1, overflow: 'auto' }}>
        {filteredItems.map((item) => {
          const PRODUCTS_PATHS = ['/products', '/categories', '/brands', '/attributes', '/attribute-sets'];
          const selected =
            item.path === '/'
              ? location.pathname === '/'
              : item.path === '/products'
                ? PRODUCTS_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
                : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <ListItem key={item.path}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={selected}
                sx={{
                  fontWeight: selected ? 'md' : undefined,
                  [`&.${listItemButtonClasses.selected}`]: {
                    bgcolor: 'background.level2',
                    color: 'text.primary',
                  },
                }}
              >
                <ListItemDecorator>{item.icon}</ListItemDecorator>
                {item.label}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'neutral.softBg',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography level="title-sm" fontWeight="lg">
            {getInitials(email ?? 'U')}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="body-sm" fontWeight="md" noWrap>
            {companyName ?? 'Empresa'}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
            {email ?? '—'}
          </Typography>
        </Box>
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => {
            logout();
            navigate('/login', { replace: true });
          }}
          title="Salir"
        >
          <LogoutRoundedIcon />
        </Button>
      </Box>
    </Sheet>
  );
};

export default Sidebar;

